/**
 * Cart upsell ("Others also bought")
 *
 * 1. <cart-upsell data-mode="automatic"> loads Shopify product recommendations for the products in the cart through
 *    the Section Rendering API (sections/cart-upsell-recommendations.liquid) and fills the carousel. Remaining slots
 *    are filled with the merchant's fallback products, rendered server side inside <template data-cart-upsell-fallback>.
 *    Manual mode is rendered fully in Liquid and needs no JavaScript for loading.
 *
 * 2. <cart-upsell-carousel> is a small horizontal carousel: the list scrolls natively (scroll snap), the arrows scroll
 *    by the number of visible cards and are hidden at the edges / when everything fits.
 *
 * 3. Products with options show a compact select per option. Changing a select resolves the matching variant, updates
 *    the price and enables/disables the "Add" button. The forms use the theme's <form is="product-form">, so adding
 *    triggers the normal cart refresh.
 *
 * The cart drawer re-renders its content on every cart change, so fresh elements are created each time. All option
 * handling therefore uses event delegation on the document.
 */
(() => {
  if (window.cartUpsellInitialized) {
    return;
  }
  window.cartUpsellInitialized = true;

  const MAX_LOOKUPS = 3; // how many cart products we ask recommendations for (most recently added first)

  /* ------------------------------------------------------------------------------------------------------------
     1. Loading recommendations
     ------------------------------------------------------------------------------------------------------------ */

  class CartUpsell extends HTMLElement {
    connectedCallback() {
      if (this.dataset.mode === 'automatic' && !this._loading) {
        this._load();
      }
    }

    async _load() {
      this._loading = true;

      const productIds = (this.dataset.productIds || '').split(',').filter(Boolean);
      const excludeIds = new Set((this.dataset.excludeIds || '').split(',').filter(Boolean));
      const limit = parseInt(this.dataset.limit, 10) || 4;
      const intent = this.dataset.intent || 'related';
      const sectionId = this.dataset.section || 'cart-upsell-recommendations';
      const seen = new Set();
      const items = [];

      for (const productId of productIds.slice(0, MAX_LOOKUPS)) {
        if (items.length >= limit) {
          break;
        }

        const doc = await this._fetchRecommendations(productId, intent, limit, sectionId);
        this._collectItems(doc, { seen, items, limit, excludeIds });
      }

      // "Complementary" products must be set up in the Search & Discovery app; if none exist fall back to "related".
      if (items.length === 0 && intent !== 'related' && productIds.length > 0) {
        const doc = await this._fetchRecommendations(productIds[0], 'related', limit, sectionId);
        this._collectItems(doc, { seen, items, limit, excludeIds });
      }

      // Fill remaining slots with the products chosen by the merchant (rendered server side).
      const fallback = this.querySelector('template[data-cart-upsell-fallback]');

      if (fallback && items.length < limit) {
        this._collectItems(fallback.content, { seen, items, limit, excludeIds });
      }

      if (!this.isConnected) {
        return; // the drawer was re-rendered while we were loading
      }

      const list = this.querySelector('[data-cart-upsell-list]');

      if (!list || items.length === 0) {
        this.remove();
        return;
      }

      list.replaceChildren(...items.map((item) => document.importNode(item, true)));
      this.hidden = false;

      const carousel = this.querySelector('cart-upsell-carousel');

      if (carousel && typeof carousel.update === 'function') {
        carousel.update();
      }
    }

    async _fetchRecommendations(productId, intent, limit, sectionId) {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const requestLimit = Math.min(10, limit + 4); // ask for a few extra so we can drop products already in the cart
      const url = `${root}recommendations/products?product_id=${productId}&limit=${requestLimit}&intent=${intent}&section_id=${sectionId}`;

      try {
        const response = await fetch(url);

        if (!response.ok) {
          return null;
        }

        return new DOMParser().parseFromString(await response.text(), 'text/html');
      } catch (error) {
        return null;
      }
    }

    _collectItems(root, { seen, items, limit, excludeIds }) {
      if (!root) {
        return;
      }

      root.querySelectorAll('[data-cart-upsell-item]').forEach((item) => {
        const productId = item.dataset.productId;

        if (!productId || seen.has(productId) || excludeIds.has(productId) || items.length >= limit) {
          return;
        }

        seen.add(productId);
        items.push(item);
      });
    }
  }

  if (!window.customElements.get('cart-upsell')) {
    window.customElements.define('cart-upsell', CartUpsell);
  }

  /* ------------------------------------------------------------------------------------------------------------
     2. Carousel
     ------------------------------------------------------------------------------------------------------------ */

  class CartUpsellCarousel extends HTMLElement {
    connectedCallback() {
      this._scroller = this.querySelector('[data-cart-upsell-list]');
      this._prevButton = this.querySelector('[data-cart-upsell-prev]');
      this._nextButton = this.querySelector('[data-cart-upsell-next]');

      if (!this._scroller) {
        return;
      }

      this._abortController = new AbortController();
      const { signal } = this._abortController;

      this._prevButton?.addEventListener('click', () => this.scrollByCards(-1), { signal });
      this._nextButton?.addEventListener('click', () => this.scrollByCards(1), { signal });
      this._scroller.addEventListener('scroll', () => this._scheduleUpdate(), { signal, passive: true });

      this._resizeObserver = new ResizeObserver(() => this.update());
      this._resizeObserver.observe(this._scroller);

      this.update();
    }

    disconnectedCallback() {
      this._abortController?.abort();
      this._resizeObserver?.disconnect();
    }

    get _cardStep() {
      const firstCard = this._scroller.querySelector('[data-cart-upsell-item]');

      if (!firstCard) {
        return this._scroller.clientWidth * 0.8;
      }

      const gap = parseFloat(getComputedStyle(this._scroller).columnGap) || 0;

      return firstCard.offsetWidth + gap;
    }

    scrollByCards(direction) {
      const scroller = this._scroller;
      const step = this._cardStep;
      const styles = getComputedStyle(scroller);
      const contentWidth = scroller.clientWidth - (parseFloat(styles.paddingInlineStart) || 0) - (parseFloat(styles.paddingInlineEnd) || 0);
      const visibleCards = Math.max(1, Math.floor((contentWidth + 1) / step));
      const rtl = document.dir === 'rtl' ? -1 : 1;

      scroller.scrollBy({
        left: direction * rtl * visibleCards * step,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      });
    }

    _scheduleUpdate() {
      if (this._frame) {
        return;
      }

      this._frame = requestAnimationFrame(() => {
        this._frame = null;
        this.update();
      });
    }

    update() {
      const scroller = this._scroller;

      if (!scroller) {
        return;
      }

      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      const scrollLeft = Math.round(Math.abs(scroller.scrollLeft));

      this.classList.toggle('is-scrollable', maxScroll > 1);

      if (this._prevButton) {
        this._prevButton.disabled = scrollLeft <= 1;
      }

      if (this._nextButton) {
        this._nextButton.disabled = scrollLeft >= maxScroll - 1;
      }

      // Keep the arrows vertically centred on the product image
      const media = scroller.querySelector('.cart-upsell__media');

      if (media && media.offsetHeight > 0) {
        this.style.setProperty('--cart-upsell-nav-top', `${media.offsetTop + media.offsetHeight / 2}px`);
      }
    }
  }

  if (!window.customElements.get('cart-upsell-carousel')) {
    window.customElements.define('cart-upsell-carousel', CartUpsellCarousel);
  }

  /* ------------------------------------------------------------------------------------------------------------
     3. Size dropdown
     ------------------------------------------------------------------------------------------------------------

     The native <select> popup is drawn by the operating system and cannot be styled, so it looks out of place next
     to the theme. <cart-upsell-select> hides the select and puts a button plus a listbox in its place, both styled
     like the rest of the drawer. The select stays in the DOM and is still the single source of truth: picking a
     value sets select.value and dispatches "change", which runs the variant logic in section 4.

     The panel is appended to <cart-upsell> (outside the horizontally scrolling carousel, which would clip it) with
     fixed positioning. It must stay inside the cart drawer: the drawer's focus trap treats any click outside of it as
     "click outside", closes the drawer and swallows the click, so a panel in <body> could never be used.
     ------------------------------------------------------------------------------------------------------------ */

  class CartUpsellSelect extends HTMLElement {
    connectedCallback() {
      this._select = this.querySelector('select');

      if (!this._select) {
        return;
      }

      if (this._trigger) {
        this._syncLabel(); // the element was moved (drawer re-render), keep the label in sync
        return;
      }

      this._trigger = document.createElement('button');
      this._trigger.type = 'button';
      this._trigger.className = 'cart-upsell__select-trigger';
      this._trigger.setAttribute('aria-haspopup', 'listbox');
      this._trigger.setAttribute('aria-expanded', 'false');

      if (this.dataset.optionLabel) {
        this._trigger.setAttribute('aria-label', this.dataset.optionLabel);
      }

      this._label = document.createElement('span');
      this._label.className = 'cart-upsell__select-value';
      this._trigger.append(this._label, this._chevron());
      this.append(this._trigger);
      this.classList.add('is-enhanced');
      this._select.setAttribute('tabindex', '-1');
      this._select.setAttribute('aria-hidden', 'true');
      this._syncLabel();

      this._trigger.addEventListener('click', () => (this._panel ? this.close() : this.open()));
      this._trigger.addEventListener('keydown', (event) => {
        if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
          event.preventDefault();
          this.open();
        }
      });
      this._select.addEventListener('change', () => this._syncLabel());
    }

    disconnectedCallback() {
      this.close();
    }

    _chevron() {
      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 10 6');
      svg.setAttribute('fill', 'none');
      svg.setAttribute('aria-hidden', 'true');
      const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', 'm1 1 4 4 4-4');
      path.setAttribute('stroke', 'currentColor');
      path.setAttribute('stroke-width', '1.2');
      svg.append(path);
      return svg;
    }

    _syncLabel() {
      if (this._label) {
        const option = this._select.selectedOptions[0];
        this._label.textContent = option ? option.textContent.trim() : '';
      }
    }

    open() {
      if (this._panel) {
        return;
      }

      document.querySelectorAll('cart-upsell-select').forEach((other) => other !== this && other.close && other.close());

      const panel = document.createElement('div');
      panel.className = 'cart-upsell-select-panel';
      panel.setAttribute('role', 'listbox');

      if (this.dataset.optionLabel) {
        panel.setAttribute('aria-label', this.dataset.optionLabel);
      }

      Array.from(this._select.options).forEach((option) => {
        const item = document.createElement('button');
        item.type = 'button';
        item.className = 'cart-upsell-select-panel__option';
        item.setAttribute('role', 'option');
        item.textContent = option.textContent.trim();
        item.dataset.value = option.value;
        item.setAttribute('aria-selected', option.selected ? 'true' : 'false');

        if (option.disabled) {
          item.disabled = true;
          item.setAttribute('aria-disabled', 'true');
        }

        item.addEventListener('click', () => this._choose(option.value));
        panel.append(item);
      });

      panel.addEventListener('keydown', (event) => this._onPanelKeydown(event));
      (this.closest('cart-upsell') || this.closest('cart-drawer') || document.body).append(panel);
      this._panel = panel;
      this._position();
      this._trigger.setAttribute('aria-expanded', 'true');
      this.classList.add('is-open');

      const selected = panel.querySelector('[aria-selected="true"]:not([disabled])') || panel.querySelector('button:not([disabled])');
      selected?.focus({ preventScroll: true });

      this._abortController = new AbortController();
      const { signal } = this._abortController;
      document.addEventListener('pointerdown', (event) => {
        if (!panel.contains(event.target) && !this.contains(event.target)) {
          this.close();
        }
      }, { signal });
      // Listen on window in the capture phase so Escape reaches us before the drawer's focus trap, which would
      // otherwise close the whole drawer instead of just the dropdown.
      window.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.stopImmediatePropagation();
          this.close();
        }
      }, { capture: true, signal });
      window.addEventListener('resize', () => this.close(), { signal });
      document.addEventListener('scroll', (event) => event.target !== panel && this.close(), { capture: true, signal });

      // The cart drawer only hides its content, it does not remove it, so disconnectedCallback would not fire and
      // the panel would be left floating over the page.
      ['dialog:before-hide', 'dialog:force-close'].forEach((name) => {
        document.addEventListener(name, () => this.close(), { signal });
      });
    }

    close() {
      if (!this._panel) {
        return;
      }

      const hadFocus = this._panel.contains(document.activeElement);
      this._abortController?.abort();
      this._panel.remove();
      this._panel = null;
      this.classList.remove('is-open');
      this._trigger?.setAttribute('aria-expanded', 'false');

      if (hadFocus) {
        this._trigger?.focus({ preventScroll: true });
      }
    }

    _choose(value) {
      this._select.value = value;
      this._select.dispatchEvent(new Event('change', { bubbles: true }));
      this._syncLabel();
      this.close();
    }

    _onPanelKeydown(event) {
      const items = Array.from(this._panel.querySelectorAll('button:not([disabled])'));
      const index = items.indexOf(document.activeElement);

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          items[Math.min(index + 1, items.length - 1)]?.focus();
          break;
        case 'ArrowUp':
          event.preventDefault();
          items[Math.max(index - 1, 0)]?.focus();
          break;
        case 'Home':
          event.preventDefault();
          items[0]?.focus();
          break;
        case 'End':
          event.preventDefault();
          items[items.length - 1]?.focus();
          break;
        case 'Tab':
          this.close();
          break;
      }
    }

    _position() {
      const rect = this._trigger.getBoundingClientRect();
      const panel = this._panel;
      panel.style.minWidth = `${rect.width}px`;
      panel.style.visibility = 'hidden';
      panel.style.insetBlockStart = '0px';
      const height = panel.offsetHeight;
      const spaceBelow = window.innerHeight - rect.bottom;
      const openUp = spaceBelow < height + 12 && rect.top > spaceBelow;
      const top = openUp ? Math.max(8, rect.top - height - 6) : rect.bottom + 6;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - panel.offsetWidth - 8);
      panel.style.insetBlockStart = `${top}px`;
      panel.style.insetInlineStart = `${left}px`;

      // A transformed ancestor (the drawer while it animates) becomes the containing block of a fixed element, so
      // correct for any offset between where the panel should be and where it ended up.
      const placed = panel.getBoundingClientRect();
      panel.style.insetBlockStart = `${top - (placed.top - top)}px`;
      panel.style.insetInlineStart = `${left - (placed.left - left)}px`;
      panel.style.maxHeight = `${Math.max(120, (openUp ? rect.top : window.innerHeight - rect.bottom) - 14)}px`;
      panel.style.visibility = '';
    }
  }

  if (!window.customElements.get('cart-upsell-select')) {
    window.customElements.define('cart-upsell-select', CartUpsellSelect);
  }

  /* ------------------------------------------------------------------------------------------------------------
     4. Variant selection
     ------------------------------------------------------------------------------------------------------------ */

  // Option changed -> resolve the variant, update price and button
  document.addEventListener('change', (event) => {
    const select = event.target.closest('[data-cart-upsell-option]');

    if (!select) {
      return;
    }

    const item = select.closest('[data-cart-upsell-item]');
    const dataScript = item ? item.querySelector('[data-cart-upsell-variants]') : null;

    if (!dataScript) {
      return;
    }

    let variants = [];

    try {
      variants = JSON.parse(dataScript.textContent);
    } catch (error) {
      return;
    }

    const selects = Array.from(item.querySelectorAll('[data-cart-upsell-option]'))
      .sort((a, b) => parseInt(a.dataset.optionIndex, 10) - parseInt(b.dataset.optionIndex, 10));
    const selectedValues = selects.map((element) => element.value);

    const variant = variants.find((candidate) => candidate.options.every((value, index) => value === selectedValues[index]));
    const idInput = item.querySelector('[data-cart-upsell-variant-id]');
    const submitButton = item.querySelector('[data-cart-upsell-submit]');
    const priceElement = item.querySelector('[data-cart-upsell-price]');
    const strings = (window.themeVariables && window.themeVariables.strings) || {};

    if (variant && variant.available) {
      if (idInput) {
        idInput.value = variant.id;
      }

      if (submitButton) {
        submitButton.disabled = false;
        submitButton.textContent = submitButton.dataset.addText || strings.addToCartButton || 'Add to cart';
      }
    } else if (submitButton) {
      submitButton.disabled = true;
      submitButton.textContent = variant
        ? (submitButton.dataset.soldOutText || strings.soldOutButton || 'Sold out')
        : (strings.unavailableButton || submitButton.dataset.soldOutText || 'Unavailable');
    }

    if (variant && priceElement) {
      priceElement.innerHTML = variant.compare_at_price
        ? `<span class="text-on-sale">${variant.price}</span> <span class="text-subdued line-through">${variant.compare_at_price}</span>`
        : `<span class="text-subdued">${variant.price}</span>`;
    }
  });
})();
